import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Truck, MapPin, CheckCircle, Navigation, AlertCircle,
  ChevronRight, ChevronLeft, Clock, Package, Wrench, Camera, X, Plus,
} from 'lucide-react';
import { employeePortalService } from '../services/api';
import { JobStatus } from '../types';
import type { JobSummaryDto, JobDetailDto } from '../types';
import { PodSubmissionModal } from '../components/pod/PodSubmissionModal';
import { RouteMap } from '../components/RelayMap';

// ─────────────────────────────────────────────
//  Status helpers
// ─────────────────────────────────────────────
const STATUS_LABELS: Record<number, string> = {
  [JobStatus.Accepted]:          'Accepted',
  [JobStatus.ContractGenerated]: 'Contract Ready',
  [JobStatus.CarrierAccepted]:    'Confirmed',
  [JobStatus.InProgress]:        'In Progress',
  [JobStatus.Completed]:         'Completed',
  [JobStatus.PodVerified]:       'POD Verified',
  [JobStatus.PaymentReleased]:   'Paid',
  [JobStatus.Cancelled]:         'Cancelled',
};

const activeStatuses = new Set([
  JobStatus.Accepted, JobStatus.ContractGenerated,
  JobStatus.CarrierAccepted, JobStatus.InProgress,
]);

function statusColor(s: JobStatus): string {
  if (activeStatuses.has(s)) return 'bg-blue-100 text-blue-700';
  if (s === JobStatus.Completed || s === JobStatus.PodVerified) return 'bg-green-100 text-green-700';
  if (s === JobStatus.PaymentReleased) return 'bg-emerald-100 text-emerald-700';
  if (s === JobStatus.Cancelled) return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-600';
}

// ─────────────────────────────────────────────
//  GPS hook — sends location every 30 seconds
// ─────────────────────────────────────────────
function useGpsTracking(
  token: string,
  jobId: string | null,
  active: boolean,
) {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const svc = useRef(employeePortalService(token));

  const sendLocation = useCallback(() => {
    if (!jobId) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition(pos);
        setGpsError(null);
        svc.current.updateLocation(
          jobId,
          pos.coords.latitude,
          pos.coords.longitude,
          pos.coords.accuracy ?? undefined,
        ).catch(() => {/* background — ignore network errors */});
      },
      (err) => setGpsError(err.message),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }, [jobId]);

  useEffect(() => {
    if (!active || !jobId) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    sendLocation();
    intervalRef.current = setInterval(sendLocation, 30_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [active, jobId, sendLocation]);

  return { position, gpsError };
}

// ─────────────────────────────────────────────
//  JOB DETAIL VIEW  (employee-simplified)
// ─────────────────────────────────────────────
function JobDetailView({
  token,
  jobId,
  onBack,
}: {
  token: string;
  jobId: string;
  onBack: () => void;
}) {
  const [job, setJob]               = useState<JobDetailDto | null>(null);
  const [loading, setLoading]       = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg]   = useState('');
  const [showPod, setShowPod]       = useState(false);

  const svc = employeePortalService(token);

  const load = useCallback(async () => {
    try {
      const data = await svc.getJob(jobId);
      setJob(data);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const isTracking = job?.status === JobStatus.InProgress;
  const { position, gpsError } = useGpsTracking(token, jobId, isTracking);

  const handleStart = async () => {
    setActionBusy(true);
    try {
      await svc.startJob(jobId);
      setActionMsg('Job started — GPS is now tracking and sharing your live location with the customer and carrier owner every 30 seconds.');
      await load();
    } catch (e: any) {
      setActionMsg(e.message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleComplete = async () => {
    setActionBusy(true);
    try {
      await svc.completeJob(jobId);
      setActionMsg('');
      await load();
    } catch (e: any) {
      setActionMsg(e.message);
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );

  if (!job) return (
    <div className="p-6 text-center text-gray-500">Job not found.</div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Back + reference */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <div className="font-semibold text-gray-900 text-sm">{job.referenceNumber}</div>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(job.status)}`}>
            {STATUS_LABELS[job.status] ?? 'Unknown'}
          </span>
        </div>
      </div>

      {/* GPS indicator */}
      {isTracking && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${gpsError ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
          <span className={`w-2 h-2 rounded-full ${gpsError ? 'bg-amber-500' : 'bg-green-500 animate-pulse'}`} />
          <Navigation className={`w-4 h-4 ${gpsError ? 'text-amber-600' : 'text-green-600'}`} />
          <span className={`text-sm font-medium ${gpsError ? 'text-amber-700' : 'text-green-700'}`}>
            {gpsError ? `GPS issue: ${gpsError}` : 'GPS tracking active — sharing location every 30s'}
          </span>
          {position && !gpsError && (
            <span className="ml-auto text-xs text-green-600">
              ±{Math.round(position.coords.accuracy)}m
            </span>
          )}
        </div>
      )}

      {/* Description */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <h2 className="font-semibold text-gray-900">{job.title}</h2>
        {job.description && <p className="text-sm text-gray-600">{job.description}</p>}

        <div className="flex flex-col gap-2 pt-1">
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Pickup</div>
              <div className="text-gray-700">{job.pickupAddress}</div>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Delivery</div>
              <div className="text-gray-700">{job.deliveryAddress}</div>
            </div>
          </div>
        </div>

        {job.goodsDescription && (
          <div className="flex items-start gap-2 text-sm pt-1 border-t border-gray-100">
            <Package className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
            <span className="text-gray-600">{job.goodsDescription}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-gray-500 pt-1 border-t border-gray-100">
          <Clock className="w-4 h-4" />
          <span>{new Date(job.scheduledAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>
          <span className="ml-auto font-medium text-gray-700">{job.distanceMiles.toFixed(1)} miles</span>
        </div>
      </div>

      {/* Route map */}
      {job.pickupLat !== 0 && (
        <div className="rounded-xl overflow-hidden border border-gray-200 h-64">
          <RouteMap
            pickupLat={job.pickupLat}
            pickupLng={job.pickupLng}
            pickupAddress={job.pickupAddress}
            deliveryLat={job.deliveryLat}
            deliveryLng={job.deliveryLng}
            deliveryAddress={job.deliveryAddress}
          />
        </div>
      )}

      {/* Action message */}
      {actionMsg && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-200">
          <AlertCircle className="w-4 h-4 text-blue-600 shrink-0" />
          <span className="text-sm text-blue-700">{actionMsg}</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col gap-2">
        {(job.status === JobStatus.CarrierAccepted || job.status === JobStatus.ContractGenerated) && (
          <button
            onClick={handleStart}
            disabled={actionBusy}
            className="w-full py-3 px-4 rounded-xl bg-blue-600 text-white font-semibold text-sm
              hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            <Navigation className="w-4 h-4" />
            {actionBusy ? 'Starting...' : 'Start Job'}
          </button>
        )}

        {job.status === JobStatus.InProgress && (
          <button
            onClick={handleComplete}
            disabled={actionBusy}
            className="w-full py-3 px-4 rounded-xl bg-amber-500 text-white font-semibold text-sm
              hover:bg-amber-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            {actionBusy ? 'Completing...' : 'Mark as Delivered'}
          </button>
        )}

        {job.status === JobStatus.Completed && (
          <button
            onClick={() => setShowPod(true)}
            className="w-full py-3 px-4 rounded-xl bg-green-600 text-white font-semibold text-sm
              hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <Package className="w-4 h-4" />
            Submit Proof of Delivery
          </button>
        )}
      </div>

      {showPod && (
        <PodSubmissionModal
          jobId={jobId}
          portalToken={token}
          onClose={() => setShowPod(false)}
          onSuccess={() => { setShowPod(false); load(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  JOB LIST  (all assigned jobs)
// ─────────────────────────────────────────────
function JobList({
  jobs,
  onSelect,
}: {
  jobs: JobSummaryDto[];
  onSelect: (id: string) => void;
}) {
  const active  = jobs.filter(j => activeStatuses.has(j.status));
  const history = jobs.filter(j => !activeStatuses.has(j.status));

  const JobCard = ({ job }: { job: JobSummaryDto }) => (
    <button
      onClick={() => onSelect(job.id)}
      className="w-full text-left bg-white border border-gray-200 rounded-xl p-4
        hover:border-blue-300 hover:shadow-sm transition-all flex items-center gap-3"
    >
      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
        <Truck className="w-5 h-5 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-semibold text-gray-900 text-sm truncate">{job.referenceNumber}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${statusColor(job.status)}`}>
            {STATUS_LABELS[job.status] ?? 'Unknown'}
          </span>
        </div>
        <div className="text-xs text-gray-500 truncate">{job.pickupAddress}</div>
        <div className="text-xs text-gray-400">→ {job.deliveryAddress}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
    </button>
  );

  if (jobs.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-sm">No jobs assigned yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {active.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Active Jobs</div>
          <div className="space-y-2">{active.map(j => <JobCard key={j.id} job={j} />)}</div>
        </div>
      )}
      {history.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Past Jobs</div>
          <div className="space-y-2">{history.map(j => <JobCard key={j.id} job={j} />)}</div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  EMPLOYEE PORTAL  —  root page  (/driver/:token or /employee/:token)
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
//  REPORT A REPAIR  (employee creates a draft repair job)
// ─────────────────────────────────────────────
function ReportRepairForm({
  token,
  onDone,
}: {
  token: string;
  onDone: () => void;
}) {
  const [reg,       setReg]       = useState('');
  const [desc,      setDesc]      = useState('');
  const [location,  setLocation]  = useState('');
  const [lat,       setLat]       = useState<number>(51.5074);
  const [lng,       setLng]       = useState<number>(-0.1278);
  const [files,     setFiles]     = useState<File[]>([]);
  const [previews,  setPreviews]  = useState<string[]>([]);
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState<string | null>(null);
  const [done,      setDone]      = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const MAX_FILES = 10;

  // GPS auto-fill
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        try {
          const resp = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const json = await resp.json();
          setLocation(json.display_name ?? `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        } catch {
          setLocation(`${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8_000 }
    );
  }, []);

  const addFiles = (incoming: File[]) => {
    const valid = incoming.filter(f => f.size <= 50 * 1024 * 1024);
    setFiles(prev => {
      const merged = [...prev, ...valid].slice(0, MAX_FILES);
      setPreviews(prevP => {
        const newP = valid.slice(0, MAX_FILES - prev.length).map(f => URL.createObjectURL(f));
        return [...prevP, ...newP].slice(0, MAX_FILES);
      });
      return merged;
    });
  };

  const removeFile = (i: number) => {
    URL.revokeObjectURL(previews[i]);
    setFiles(p => p.filter((_, idx) => idx !== i));
    setPreviews(p => p.filter((_, idx) => idx !== i));
  };

  const toBase64 = (file: File): Promise<{ base64: string; mimeType: string }> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve({ base64: dataUrl.split(',')[1], mimeType: file.type || 'image/jpeg' });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSave = async () => {
    if (!reg.trim())  { setErr('Vehicle registration is required.'); return; }
    if (!desc.trim()) { setErr('Please describe the issue.'); return; }
    setSaving(true); setErr(null);
    try {
      const svc = employeePortalService(token);
      const images = files.length > 0 ? await Promise.all(files.map(toBase64)) : [];
      await svc.createRepairDraft({ vehicleRegistration: reg.trim().toUpperCase(), issueDescription: desc.trim(), locationAddress: location.trim(), pickupLat: lat, pickupLng: lng, images });
      setDone(true);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center space-y-3">
        <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
        <p className="font-semibold text-green-800">Repair job saved!</p>
        <p className="text-sm text-green-700">Your carrier owner has been notified and will review and submit the job for workshop quotes.</p>
        <button onClick={onDone} className="mt-2 text-sm text-green-700 underline">Back to Jobs</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onDone} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <div className="font-semibold text-gray-900 text-sm">Report a Repair</div>
          <p className="text-xs text-gray-500">Saved as a draft — your carrier owner will submit it</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
        <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
        <span>Vehicle location auto-filled from GPS. Update below if needed.</span>
      </div>

      {/* Photos / videos */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          <Camera className="inline w-3.5 h-3.5 mr-1" />
          Photos &amp; Videos <span className="text-gray-400 font-normal">(optional, up to {MAX_FILES})</span>
        </label>
        {previews.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mb-2">
            {previews.map((src, i) => (
              <div key={i} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50" style={{ aspectRatio: '1' }}>
                {files[i]?.type.startsWith('video/') ? (
                  <video src={src} className="w-full h-full object-cover" muted playsInline />
                ) : (
                  <img src={src} alt="" className="w-full h-full object-cover" />
                )}
                <button type="button" onClick={() => removeFile(i)}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        {previews.length < MAX_FILES && (
          <button type="button" onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-300 hover:border-orange-400 rounded-xl p-4 text-center text-sm text-gray-400 transition-colors flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Add photo or video
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden"
          onChange={e => { addFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
      </div>

      {/* Vehicle reg */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Registration</label>
        <input value={reg} onChange={e => setReg(e.target.value.toUpperCase())}
          placeholder="AB12 CDE" maxLength={10}
          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-400 uppercase" />
      </div>

      {/* Issue description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Issue Description</label>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3}
          placeholder="Describe the issue — warning light, strange noise, won't start..."
          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-400 resize-none" />
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <MapPin className="inline w-3.5 h-3.5 mr-1 text-blue-500" /> Vehicle Location
        </label>
        <input value={location} onChange={e => setLocation(e.target.value)}
          placeholder="Vehicle address or postcode"
          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-400" />
      </div>

      {err && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />{err}
        </div>
      )}

      <button onClick={handleSave} disabled={saving}
        className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2">
        {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Wrench className="w-4 h-4" />}
        {saving ? 'Saving…' : 'Save Repair Draft'}
      </button>
    </div>
  );
}

export default function EmployeePortalPage() {
  const { token = '' } = useParams<{ token: string }>();

  const [jobs, setJobs]           = useState<JobSummaryDto[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [showRepairForm, setShowRepairForm] = useState(false);
  const [gpsWarning, setGpsWarning] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setError('Invalid portal link.'); setLoading(false); return; }

    employeePortalService(token)
      .getJobs()
      .then(setJobs)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Idle GPS — posts location every 30s so the carrier's Fleet Map shows this employee.
  useEffect(() => {
    if (!token) return;

    if (!navigator.geolocation) {
      setGpsWarning('Location sharing is not supported on this device. Your carrier cannot see you on the Fleet Map.');
      return;
    }

    const svc = employeePortalService(token);
    const send = () => navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsWarning(null);
        svc.updateIdleLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy ?? undefined)
          .catch(() => {});
      },
      (err) => {
        const msg = err.code === 1
          ? 'Location permission denied. Please allow location access in your browser settings so your carrier can see you on the Fleet Map.'
          : err.code === 2
          ? 'Unable to determine your location. Please check that GPS is enabled on your device.'
          : 'Location sharing timed out. Please check your GPS signal.';
        setGpsWarning(msg);
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
    send();
    const id = setInterval(send, 30_000);
    return () => clearInterval(id);
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
          <Truck className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="font-semibold text-gray-900 text-sm">TransitDeal Employee</div>
          <div className="text-xs text-gray-400">Assignment Portal</div>
        </div>
      </div>

      {gpsWarning && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Location sharing unavailable</p>
            <p className="text-xs text-amber-700 mt-0.5">{gpsWarning}</p>
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto p-4">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        )}

        {error && (
          <div className="mt-8 p-6 bg-red-50 border border-red-200 rounded-xl text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-700 font-medium">{error}</p>
            <p className="text-xs text-red-500 mt-1">
              This link may be invalid or expired. Ask your manager for a new one.
            </p>
          </div>
        )}

        {!loading && !error && (
          selectedJobId ? (
            <JobDetailView
              token={token}
              jobId={selectedJobId}
              onBack={() => setSelectedJobId(null)}
            />
          ) : showRepairForm ? (
            <ReportRepairForm token={token} onDone={() => setShowRepairForm(false)} />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-lg font-semibold text-gray-900">My Jobs</h1>
                <button
                  onClick={() => setShowRepairForm(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold transition-colors"
                >
                  <Wrench className="w-3.5 h-3.5" /> Report Repair
                </button>
              </div>
              <JobList jobs={jobs} onSelect={setSelectedJobId} />
            </>
          )
        )}
      </div>
    </div>
  );
}
